// Support ticket limits and the reply checks. Shared so the checks run in the
// browser while typing and the same rules can be tested in Node.

export const MAX_TICKET_CHARS = 20_000
export const MAX_REPLY_CHARS = 12_000

/** Ticket status → label. The key order is the order shown in the status picker. */
export const STATUS_LABELS = {
  open: 'New / Open',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on customer',
  waiting_third_party: 'Waiting on third party',
  platform_dependency: 'ERP / platform dependency',
  awaiting_approval: 'Awaiting customer approval',
  resolved: 'Resolved',
  reopened: 'Reopened',
  closed: 'Closed',
  cancelled: 'Cancelled',
}
export const TICKET_STATUSES = Object.keys(STATUS_LABELS)

export const PRIORITY_LABELS = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }

/**
 * Quality checks for a reply, scored live in the editor.
 * @param {string} reply
 * @param {{ keyTerms?: string[] } | null} analysis
 * @returns {{ id: string, label: string, ok: boolean }[]}
 */
export function replyChecks(reply, analysis) {
  const text = String(reply ?? '')
  const lower = text.toLowerCase()
  const terms = (analysis?.keyTerms ?? []).map((t) => String(t).toLowerCase()).filter(Boolean)
  return [
    { id: 'greets', label: 'Greets the customer', ok: /^\s*(subject:[^\n]*\n+\s*)?(hi|hello|dear)\b/i.test(text) },
    { id: 'details', label: 'Uses details from the ticket', ok: terms.some((t) => lower.includes(t)) },
    { id: 'asks', label: 'Asks for what is still missing', ok: /\?|please (confirm|share|send|provide)/i.test(text) },
    { id: 'steps', label: 'Lists clear next steps', ok: /next steps?|\bwe(['’]ll| will| can)\b|^\s*\d+[.)]\s/im.test(text) },
    { id: 'ownership', label: 'Shows DCKAP owns it', ok: /\bwe(['’]ll| will| are| have|['’]re| can)\b|\bour team\b/i.test(text) },
    { id: 'update', label: 'Says when they will hear back', ok: /\b(update you|hear back|keep you (posted|updated)|follow up)\b|\[day\]/i.test(text) },
    { id: 'closes', label: 'Closes politely', ok: /\b(thanks|thank you|regards|sincerely)\b[,!]?\s*(\n|$)/i.test(text) },
  ]
}

/** 0–100 from the checks; 0 for an empty reply. */
export function replyScore(reply, analysis) {
  if (!String(reply ?? '').trim()) return 0
  const checks = replyChecks(reply, analysis)
  return Math.round((checks.filter((c) => c.ok).length / checks.length) * 100)
}

/**
 * Split internal notes into labelled sections for display. Reads the layout the
 * draft prompt asks for ("Label:" line, then "- " bullets). Older or off-format
 * notes still come out as readable paragraphs and bullets, with markdown
 * emphasis marks removed.
 * @param {string} notes
 * @returns {{ title: string, items: string[] }[]}
 */
export function parseNotes(notes) {
  const clean = (line) =>
    line
      .replace(/^\s*#{1,6}\s+/, '')
      .replace(/\*\*|__/g, '')
      .replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '')
      .trim()
  const sections = []
  let current = null
  for (const raw of String(notes ?? '').split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const text = clean(line)
    if (!text) continue
    const isBullet = /^(?:[-*•]|\d+[.)])\s+/.test(line)
    const isLabel = !isBullet && (/^\s*#{1,6}\s/.test(line) || /^[^:]{2,60}:$/.test(text))
    if (isLabel) {
      current = { title: text.replace(/:$/, ''), items: [] }
      sections.push(current)
    } else {
      if (!current) {
        current = { title: '', items: [] }
        sections.push(current)
      }
      current.items.push(text)
    }
  }
  return sections
}
